// PO7-S5 (docs/build-briefs/PO7-S5-invoice-scan-receive.md; DEC-411; design/mockups/invoice-scan-receive.html). The
// "Receive" screen that reads an invoice: photograph it (or pick a file), watch it being read, then tap Accept on each
// line and Finish. This module draws it and wires the camera, the upload, the reading wait and the posting; every rule
// (what is handled, what blocks Finish, which unit a line posts in, what posts once) lives in receiveScanModel.ts, where it
// is tested without a DOM.
//
// What it guarantees, each one checked by e2e/po7-s5-invoice-scan-receive.spec.ts:
//   - Touch only. Every control is a plain button of 44 px or more. There is no text field, no keyboard or mouse listener
//     and no hover rule. The one file input is hidden and opened by a button.
//   - Nothing is received because an invoice said so: a line starts Pending and posts only after a tap, through the
//     ordinary receipt command, once.
//   - No card data, no cost: the screen shows quantities only, and the routes behind it carry no price.
//   - The camera is requested only when Scan invoice is tapped, and every track stops on Close camera, Back, Finish and
//     whenever the screen is left (stop()), which sign-out runs.
//   - Page previews are drawn on canvases (the Content-Security-Policy is unchanged and nothing loads from a CDN).
//
// Text is set with textContent only (an invoice description is read off a photo and can say anything).

import {
  getLiquorProduct,
  getReceiveScan,
  listLiquorProducts,
  lookupBarcode,
  postScanReceipt,
  retryReceiveScanReading,
  uploadInvoiceAttachment,
  uploadReceiveScan,
  type LiquorProductDto,
  type LineReceivingStateDto,
  type ReceiveScanDraftDto,
  type ReceiveScanOrderLineTargetDto,
  type BarcodeMatchDto,
  type ReceiveScanTargetDto,
} from "./api.ts";
import { createFrameDecoder, type FrameDecoder } from "./barcodeDecoder.ts";
import { createScanGate } from "./barcodeReceiveModel.ts";
import { checkInvoiceFileBeforeUpload } from "./invoiceAttachmentsDisplay.ts";
import { formatReceivingQuantity } from "./purchasingDisplay.ts";
import {
  MARK_TEXT,
  NO_VALUE,
  acceptLine,
  canAccept,
  countScannedUnit,
  finishBlocker,
  handledCount,
  initLines,
  isZeroQuantity,
  linkLine,
  lineMarks,
  markNotReceived,
  packedReading,
  runFinish,
  stepLine,
  summarize,
  undoLine,
  type FinishContext,
  type FinishSummary,
  type ProductReceiveInfo,
  type ScanLine,
} from "./receiveScanModel.ts";
import { buildJpegPdf } from "./receiveScanPdf.ts";
import type { Session } from "./session.ts";

export type ReceiveScanOrigin =
  | {
      kind: "order";
      orderId: string;
      vendorName: string;
      /** The order's lines as the receiving screen holds them. */
      lines: readonly LineReceivingStateDto[];
    }
  | { kind: "none" };

export interface ReceiveScanDeps {
  root: HTMLElement;
  session: () => Session;
  hasPermission: (permissionId: string) => boolean;
  showToast: (message: string, variant: "success" | "danger") => void;
  /** Leaves the screen for the one it was opened from. */
  onLeave: (origin: ReceiveScanOrigin) => void;
  /** The done screen's Review invoice (an order's own invoice, for a person who may see costs). */
  onReviewInvoice: (orderId: string) => void;
  /** Receipts posted: the screen's caller offers to clear a stock flag for the items. */
  onReceived: (liquorProductIds: readonly string[]) => void;
  /** Reading is polled this often, and given up on after this long. Overridable so a test need not wait. */
  pollIntervalMs?: number;
  pollDeadlineMs?: number;
}

export interface ReceiveScanController {
  open(origin: ReceiveScanOrigin): void;
  /** Stops the camera and the reading wait and forgets the visit. Safe to call at any time. */
  stop(): void;
  /** The barcode hook (B6): a scan of an item ticks the matching lines toward the invoice. */
  countProduct(liquorProductId: string, delta: number): void;
}

type View = "start" | "capture" | "pages" | "reading" | "failed" | "vendor" | "review" | "done";

interface ImagePage {
  kind: "image";
  jpeg: Uint8Array;
  width: number;
  height: number;
  preview: HTMLCanvasElement;
}
interface PdfPage {
  kind: "pdf";
  file: File;
}
type Page = ImagePage | PdfPage;

type Sheet =
  { kind: "link"; lineNumber: number } | { kind: "vendor" } | { kind: "discard" } | undefined;

const RECEIPT_PERMISSION = "inv.receipt.record";
const COST_PERMISSION = "inv.cost.view";
const MAX_LONG_EDGE = 2000;
const THUMB_LONG_EDGE = 900;
const JPEG_QUALITY = 0.85;
const SVG_NS = "http://www.w3.org/2000/svg";

function el<K extends keyof HTMLElementTagNameMap>(
  tag: K,
  className: string,
  testId?: string,
  text?: string,
): HTMLElementTagNameMap[K] {
  const node = document.createElement(tag);
  if (className !== "") {
    node.className = className;
  }
  if (testId !== undefined) {
    node.dataset["testid"] = testId;
  }
  if (text !== undefined) {
    node.textContent = text;
  }
  return node;
}

function button(
  className: string,
  testId: string,
  text: string,
  onTap: () => void,
): HTMLButtonElement {
  const node = el("button", className, testId, text);
  node.type = "button";
  node.addEventListener("click", onTap);
  return node;
}

function icon(path: string): SVGSVGElement {
  const svg = document.createElementNS(SVG_NS, "svg");
  svg.setAttribute("class", "receive-scan-ico");
  svg.setAttribute("viewBox", "0 0 22 22");
  svg.setAttribute("aria-hidden", "true");
  const p = document.createElementNS(SVG_NS, "path");
  p.setAttribute("d", path);
  p.setAttribute("fill", "none");
  p.setAttribute("stroke", "currentColor");
  p.setAttribute("stroke-width", "1.8");
  p.setAttribute("stroke-linecap", "round");
  p.setAttribute("stroke-linejoin", "round");
  svg.append(p);
  return svg;
}

const ICON_CAMERA = "M3 6h3l1.5-2h7L16 6h3v11H3zM11 14a3 3 0 100-6 3 3 0 000 6z";
const ICON_BARCODE = "M3 7V4h3M16 4h3v3M19 15v3h-3M6 18H3v-3M7 7v8M10 7v8M13 7v8M16 7v8";
const ICON_FILE = "M5 2h9l4 4v14H5zM14 2v4h4";
const ICON_CHECK = "M4 12l5 5 9-11";
const ICON_WARN = "M11 3l9 16H2z";

function tag(text: string, kind: "" | "ok" | "warn", testId?: string): HTMLSpanElement {
  const node = el("span", `receive-scan-tag${kind === "" ? "" : ` is-${kind}`}`, testId, text);
  return node;
}

function card(title: string, tags: HTMLElement[] = [], actions: HTMLElement[] = []): HTMLElement {
  const wrap = el("section", "receive-scan-card");
  const head = el("div", "receive-scan-card-head");
  head.append(el("h2", "receive-scan-card-title", undefined, title), ...tags, ...actions);
  wrap.append(head);
  return wrap;
}

function lineCountText(count: number): string {
  return `${count} ${count === 1 ? "line" : "lines"}`;
}

function pageCountText(count: number): string {
  return `${count} ${count === 1 ? "page" : "pages"}`;
}

function drawScaled(source: CanvasImageSource, width: number, height: number, longEdge: number) {
  const scale = Math.min(1, longEdge / Math.max(width, height));
  const canvas = document.createElement("canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const context = canvas.getContext("2d");
  if (context === null) {
    throw new Error("no 2d canvas");
  }
  context.drawImage(source, 0, 0, canvas.width, canvas.height);
  return canvas;
}

async function canvasToJpeg(canvas: HTMLCanvasElement): Promise<Uint8Array> {
  const blob = await new Promise<Blob | null>((resolve) =>
    canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY),
  );
  if (blob === null) {
    throw new Error("could not encode the page");
  }
  return new Uint8Array(await blob.arrayBuffer());
}

export function createReceiveScan(deps: ReceiveScanDeps): ReceiveScanController {
  const pollIntervalMs = deps.pollIntervalMs ?? 1000;
  const pollDeadlineMs = deps.pollDeadlineMs ?? 180_000;

  let origin: ReceiveScanOrigin = { kind: "none" };
  let isOpen = false;
  let view: View = "start";
  let pages: Page[] = [];
  let selectedPage = 0;
  let pagesVersion = 0;
  let uploadedVersion = -1;
  let uploadKey: string | null = null;
  let attachmentId: string | null = null;
  let working = false;
  let notice: string | null = null;
  let camera: "off" | "starting" | "on" | "blocked" = "off";
  let facing: "environment" | "user" = "environment";
  let canFlip = false;
  let stream: MediaStream | undefined;
  let draft: ReceiveScanDraftDto | null = null;
  let vendorId: string | null = null;
  let lines: ScanLine[] = [];
  let readingMessage: string | null = null;
  let readingRetryable = false;
  let finishing = false;
  let summary: FinishSummary | null = null;
  let sheet: Sheet;
  let catalog: LiquorProductDto[] | undefined;
  let catalogFailed = false;
  let pollToken = 0;
  let previewIndex = 0;
  // The barcode hook, now live (B6 merged): a scan ticks the matching line toward the invoice quantity.
  let barcode: "off" | "starting" | "on" | "blocked" = "off";
  let barcodeStream: MediaStream | undefined;
  let barcodeLoop = 0;
  let barcodeTimer: ReturnType<typeof setTimeout> | undefined;
  let barcodeHint = "";
  let barcodeHit: { name: string; progress: string } | null = null;
  let frameDecoder: FrameDecoder | undefined;
  let scanGate = createScanGate();
  const barcodeVideo = el("video", "receive-scan-video", "receive-scan-barcode-video");
  barcodeVideo.playsInline = true;
  barcodeVideo.muted = true;
  barcodeVideo.autoplay = true;
  const products = new Map<string, ProductReceiveInfo>();
  const keySource = (): string => crypto.randomUUID();

  const root = deps.root;
  root.classList.add("receive-scan-root");
  const part = <T extends HTMLElement>(selector: string): T => {
    const found = root.querySelector<T>(selector);
    if (found === null) {
      throw new Error(`receive scan screen is missing ${selector}`);
    }
    return found;
  };
  const header = part<HTMLElement>(".receive-scan-header");
  const left = part<HTMLElement>(".receive-scan-left");
  const right = part<HTMLElement>(".receive-scan-right");
  const backdrop = part<HTMLElement>(".receive-scan-backdrop");
  const fileInput = part<HTMLInputElement>('input[type="file"]');
  const video = el("video", "receive-scan-video", "receive-scan-video");
  video.playsInline = true;
  video.muted = true;
  video.autoplay = true;

  const hasOrder = (): boolean => origin.kind === "order";

  // ---- camera -------------------------------------------------------------------------------------------------

  function stopCamera(): void {
    if (stream !== undefined) {
      for (const track of stream.getTracks()) {
        track.stop();
      }
      stream = undefined;
    }
    video.srcObject = null;
    camera = "off";
  }

  async function startCamera(): Promise<void> {
    stopCamera();
    camera = "starting";
    renderLeft();
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: facing }, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
    } catch {
      camera = "blocked";
      renderLeft();
      renderRight();
      return;
    }
    if (!isOpen || view !== "capture") {
      stopCamera();
      return;
    }
    video.srcObject = stream;
    try {
      await video.play();
    } catch {
      // A browser that refuses autoplay still shows the first frame once the stream is attached.
    }
    camera = "on";
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      canFlip = devices.filter((device) => device.kind === "videoinput").length > 1;
    } catch {
      canFlip = false;
    }
    renderLeft();
    renderRight();
  }

  async function snap(): Promise<void> {
    if (camera !== "on" || video.videoWidth === 0) {
      return;
    }
    const full = drawScaled(video, video.videoWidth, video.videoHeight, MAX_LONG_EDGE);
    const jpeg = await canvasToJpeg(full);
    const page: ImagePage = {
      kind: "image",
      jpeg,
      width: full.width,
      height: full.height,
      preview: drawScaled(full, full.width, full.height, THUMB_LONG_EDGE),
    };
    if (replacing !== undefined) {
      pages[replacing] = page;
      selectedPage = replacing;
      replacing = undefined;
    } else {
      pages.push(page);
      selectedPage = pages.length - 1;
    }
    pagesVersion += 1;
    stopCamera();
    view = "pages";
    render();
  }

  // The index of the page the next shot replaces (Retake), else undefined (Add page).
  let replacing: number | undefined;

  function openCamera(replaceIndex?: number): void {
    replacing = replaceIndex;
    view = "capture";
    render();
    void startCamera();
  }

  // ---- barcode (the B6 hook) ----------------------------------------------------------------------------------

  function stopBarcode(): void {
    barcodeLoop += 1;
    if (barcodeTimer !== undefined) {
      clearTimeout(barcodeTimer);
      barcodeTimer = undefined;
    }
    if (barcodeStream !== undefined) {
      for (const track of barcodeStream.getTracks()) {
        track.stop();
      }
      barcodeStream = undefined;
    }
    barcodeVideo.srcObject = null;
    barcode = "off";
    barcodeHint = "";
    barcodeHit = null;
  }

  async function startBarcode(): Promise<void> {
    if (view !== "review") {
      return;
    }
    stopBarcode();
    const generation = barcodeLoop;
    barcode = "starting";
    renderHeader();
    renderRight();
    try {
      barcodeStream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { ideal: "environment" }, width: { ideal: 1280 } },
        audio: false,
      });
    } catch {
      if (generation === barcodeLoop) {
        barcode = "blocked";
        renderRight();
      }
      return;
    }
    if (generation !== barcodeLoop || !isOpen || view !== "review") {
      stopBarcode();
      return;
    }
    barcodeVideo.srcObject = barcodeStream;
    try {
      await barcodeVideo.play();
    } catch {
      // The first frame still shows once the stream is attached.
    }
    if (frameDecoder === undefined) {
      frameDecoder = await createFrameDecoder(
        window as unknown as Parameters<typeof createFrameDecoder>[0],
        () => {
          const canvas = document.createElement("canvas");
          const context = canvas.getContext("2d", { willReadFrequently: true });
          if (context === null) {
            throw new Error("canvas 2d context unavailable");
          }
          return { canvas, context };
        },
      );
    }
    if (generation !== barcodeLoop) {
      return;
    }
    scanGate = createScanGate();
    barcode = "on";
    renderRight();
    barcodeTimer = setTimeout(() => void barcodeTick(generation), 150);
  }

  async function barcodeTick(generation: number): Promise<void> {
    if (generation !== barcodeLoop || frameDecoder === undefined || !isOpen) {
      return;
    }
    if (barcodeVideo.readyState >= 2 && !finishing) {
      let code: string | null;
      try {
        code = await frameDecoder.decode(barcodeVideo);
      } catch {
        code = null;
      }
      if (generation !== barcodeLoop) {
        return;
      }
      if (code !== null && scanGate.accept(code, Date.now())) {
        await handleBarcode(code, generation);
      }
    }
    if (generation === barcodeLoop) {
      barcodeTimer = setTimeout(() => void barcodeTick(generation), 150);
    }
  }

  async function handleBarcode(code: string, generation: number): Promise<void> {
    const session = deps.session();
    const result = await lookupBarcode(session.apiBaseUrl, session.tenantId, code);
    if (generation !== barcodeLoop) {
      return;
    }
    if (result.status !== 200) {
      barcodeHint = result.body?.message ?? "Could not check this barcode";
      barcodeHit = null;
    } else if (result.body.match === null) {
      barcodeHint = "Barcode not found";
      barcodeHit = null;
    } else {
      tickMatch(result.body.match);
    }
    renderRight();
  }

  function tickMatch(match: BarcodeMatchDto): void {
    const bottles = match.matchedBy === "case" ? match.caseSize : 1;
    if (bottles === null) {
      barcodeHint = `${match.name}: case size unknown`;
      barcodeHit = null;
      return;
    }
    const countable = lines.some(
      (candidate) =>
        candidate.target?.liquorProductId === match.liquorProductId &&
        !candidate.posted &&
        candidate.status !== "not_received",
    );
    if (!countable) {
      barcodeHint = `${match.name}: not on this invoice`;
      barcodeHit = null;
      return;
    }
    const next = countScannedUnit(lines, match.liquorProductId, bottles);
    lines = next;
    const line = lines.find(
      (candidate) =>
        candidate.target?.liquorProductId === match.liquorProductId && candidate.count !== null,
    );
    barcodeHint = "";
    barcodeHit = {
      name: match.name,
      progress:
        line === undefined
          ? NO_VALUE
          : `${formatReceivingQuantity(line.count ?? "0")} of ${formatReceivingQuantity(line.invoiceQuantity ?? "0")}`,
    };
    renderLeft();
  }

  // ---- file picker --------------------------------------------------------------------------------------------

  async function pageFromImageFile(file: File): Promise<ImagePage | undefined> {
    try {
      const bitmap = await createImageBitmap(file);
      const full = drawScaled(bitmap, bitmap.width, bitmap.height, MAX_LONG_EDGE);
      bitmap.close();
      return {
        kind: "image",
        jpeg: await canvasToJpeg(full),
        width: full.width,
        height: full.height,
        preview: drawScaled(full, full.width, full.height, THUMB_LONG_EDGE),
      };
    } catch {
      return undefined;
    }
  }

  async function takeFiles(files: readonly File[]): Promise<void> {
    if (files.length === 0) {
      return;
    }
    const pdf = files.find(
      (file) => file.type === "application/pdf" || file.name.toLowerCase().endsWith(".pdf"),
    );
    if (pdf !== undefined) {
      const check = checkInvoiceFileBeforeUpload(pdf);
      if (!check.ok) {
        notice = check.message;
        render();
        return;
      }
      pages = [{ kind: "pdf", file: pdf }];
    } else {
      const added: ImagePage[] = [];
      for (const file of files) {
        const page = await pageFromImageFile(file);
        if (page === undefined) {
          notice = "This image could not be read";
        } else {
          added.push(page);
        }
      }
      if (added.length === 0) {
        render();
        return;
      }
      pages = pages.some((page) => page.kind === "pdf") ? added : [...pages, ...added];
    }
    selectedPage = pages.length - 1;
    pagesVersion += 1;
    stopCamera();
    view = "pages";
    notice = null;
    render();
  }

  fileInput.addEventListener("change", () => {
    const picked = [...(fileInput.files ?? [])];
    fileInput.value = "";
    void takeFiles(picked);
  });

  function chooseFile(): void {
    fileInput.click();
  }

  // ---- upload and the reading wait ----------------------------------------------------------------------------

  async function pagesAsFile(): Promise<{ file: File; contentType: string }> {
    const first = pages[0];
    if (first !== undefined && first.kind === "pdf") {
      return { file: first.file, contentType: "application/pdf" };
    }
    const images = pages.filter((page): page is ImagePage => page.kind === "image");
    const bytes = buildJpegPdf(images);
    const name = `invoice-${images.length}-${images.length === 1 ? "page" : "pages"}.pdf`;
    return {
      file: new File([bytes as BlobPart], name, { type: "application/pdf" }),
      contentType: "application/pdf",
    };
  }

  async function readInvoice(): Promise<void> {
    if (pages.length === 0 || working) {
      return;
    }
    working = true;
    notice = null;
    stopCamera();
    view = "reading";
    readingMessage = null;
    render();
    const session = deps.session();
    try {
      if (attachmentId === null || uploadedVersion !== pagesVersion) {
        if (uploadKey === null || uploadedVersion !== pagesVersion) {
          uploadKey = keySource();
        }
        const { file, contentType } = await pagesAsFile();
        let newId: string | undefined;
        let failure: string | undefined;
        if (origin.kind === "order") {
          const result = await uploadInvoiceAttachment(
            session.apiBaseUrl,
            session.tenantId,
            origin.orderId,
            file,
            contentType,
            uploadKey,
          );
          if (result.status === 200 || result.status === 201) {
            newId = result.body.attachment.id;
          } else {
            failure = result.body?.message ?? "Could not send this invoice";
          }
        } else {
          const result = await uploadReceiveScan(
            session.apiBaseUrl,
            session.tenantId,
            file,
            file.name,
            contentType,
            uploadKey,
          );
          if (result.status === 200 || result.status === 201) {
            newId = result.body.attachmentId;
          } else {
            failure = result.body?.message ?? "Could not send this invoice";
          }
        }
        if (newId === undefined) {
          working = false;
          view = "failed";
          readingMessage = failure ?? "Could not send this invoice";
          readingRetryable = true;
          render();
          return;
        }
        attachmentId = newId;
        uploadedVersion = pagesVersion;
      }
    } catch {
      working = false;
      view = "failed";
      readingMessage = "Could not send this invoice";
      readingRetryable = true;
      render();
      return;
    }
    working = false;
    void waitForReading();
  }

  async function waitForReading(): Promise<void> {
    const token = (pollToken += 1);
    const started = Date.now();
    const session = deps.session();
    const id = attachmentId;
    if (id === null) {
      return;
    }
    while (isOpen && token === pollToken) {
      const result = await getReceiveScan(session.apiBaseUrl, session.tenantId, id);
      if (!isOpen || token !== pollToken) {
        return;
      }
      if (result.status !== 200) {
        view = "failed";
        readingMessage = result.body?.message ?? "Could not read this invoice";
        readingRetryable = true;
        render();
        return;
      }
      const scan = result.body.scan;
      if (scan.reading.status === "drafted" && scan.draft !== null) {
        takeDraft(scan.draft);
        return;
      }
      if (scan.reading.status === "failed" || scan.reading.status === "unavailable") {
        view = "failed";
        readingMessage = scan.reading.message ?? "This invoice could not be read";
        readingRetryable =
          scan.reading.status === "failed" ? scan.reading.retryable === true : true;
        render();
        return;
      }
      if (Date.now() - started > pollDeadlineMs) {
        view = "failed";
        readingMessage = "Still reading";
        readingRetryable = true;
        render();
        return;
      }
      await new Promise((resolve) => setTimeout(resolve, pollIntervalMs));
    }
  }

  function takeDraft(next: ReceiveScanDraftDto): void {
    draft = next;
    lines = initLines(next);
    if (origin.kind === "order") {
      vendorId = next.orderVendorId;
      view = "review";
    } else {
      view = "vendor";
    }
    render();
  }

  async function retryReading(): Promise<void> {
    const id = attachmentId;
    if (id === null) {
      view = "pages";
      render();
      return;
    }
    const session = deps.session();
    view = "reading";
    render();
    const result = await retryReceiveScanReading(session.apiBaseUrl, session.tenantId, id);
    if (result.status !== 202 && result.status !== 409) {
      view = "failed";
      readingMessage = result.body?.message ?? "Could not ask again";
      readingRetryable = true;
      render();
      return;
    }
    void waitForReading();
  }

  function cancelReading(): void {
    pollToken += 1;
    view = "pages";
    render();
  }

  // ---- vendor (no order) --------------------------------------------------------------------------------------

  async function confirmVendor(id: string): Promise<void> {
    const attachment = attachmentId;
    if (attachment === null || working) {
      return;
    }
    working = true;
    render();
    const session = deps.session();
    const result = await getReceiveScan(session.apiBaseUrl, session.tenantId, attachment, id);
    working = false;
    if (result.status !== 200 || result.body.scan.draft === null) {
      notice = result.body?.message ?? "Could not load this vendor";
      render();
      return;
    }
    vendorId = id;
    draft = result.body.scan.draft;
    lines = initLines(draft);
    sheet = undefined;
    notice = null;
    view = "review";
    render();
  }

  // ---- lines --------------------------------------------------------------------------------------------------

  function replaceLine(lineNumber: number, change: (line: ScanLine) => ScanLine): void {
    lines = lines.map((line) => (line.lineNumber === lineNumber ? change(line) : line));
    renderLeft();
    renderRight();
  }

  function finishContext(): FinishContext {
    return {
      orderId: origin.kind === "order" ? origin.orderId : null,
      orderVendorId: draft?.orderVendorId ?? null,
      vendorId: origin.kind === "order" ? null : vendorId,
      invoiceNumber: draft?.invoiceNumber ?? null,
      receiveReference: draft?.receiveReference ?? null,
      duplicateRecorded: draft?.duplicateRecorded ?? false,
    };
  }

  function productInfoFrom(product: LiquorProductDto): ProductReceiveInfo {
    return {
      unitPriceCents: product.unit_price_cents,
      containerType: product.container_type,
      sizeValue: product.size_value,
      sizeUnit: product.size_unit,
    };
  }

  async function ensureProducts(ids: readonly string[]): Promise<void> {
    const session = deps.session();
    const missing = [...new Set(ids)].filter((id) => !products.has(id));
    const results = await Promise.all(
      missing.map((id) => getLiquorProduct(session.apiBaseUrl, session.tenantId, id)),
    );
    missing.forEach((id, index) => {
      const result = results[index];
      if (result !== undefined && result.status === 200) {
        products.set(id, productInfoFrom(result.body.liquorProduct));
      }
    });
  }

  async function finish(): Promise<void> {
    if (finishing || finishBlocker(lines, finishContext()) !== null) {
      return;
    }
    finishing = true;
    stopCamera();
    stopBarcode();
    renderHeader();
    renderLeft();
    renderRight();
    const accepted = lines.filter((line) => line.status === "accepted" && !line.posted);
    await ensureProducts(
      accepted.flatMap((line) => (line.target === null ? [] : [line.target.liquorProductId])),
    );
    const session = deps.session();
    const outcome = await runFinish({
      lines,
      context: finishContext(),
      products,
      mintKey: keySource,
      post: async (requestBody) => {
        const result = await postScanReceipt(session.apiBaseUrl, session.tenantId, requestBody);
        return { status: result.status, message: result.body?.message ?? null };
      },
      onLine: (line) => {
        lines = lines.map((existing) =>
          existing.lineNumber === line.lineNumber ? { ...line } : existing,
        );
        renderLeft();
      },
    });
    finishing = false;
    lines = outcome.lines;
    if (outcome.duplicate && draft !== null) {
      draft = { ...draft, duplicateRecorded: true };
    }
    if (outcome.complete) {
      summary = summarize(lines);
      view = "done";
      deps.onReceived(
        lines.flatMap((line) =>
          line.posted && line.target !== null ? [line.target.liquorProductId] : [],
        ),
      );
    }
    render();
  }

  // ---- link sheet ---------------------------------------------------------------------------------------------

  async function ensureCatalog(): Promise<void> {
    if (catalog !== undefined || catalogFailed) {
      return;
    }
    const session = deps.session();
    const result = await listLiquorProducts(session.apiBaseUrl, session.tenantId, {
      activeOnly: true,
    });
    if (result.status === 200) {
      catalog = result.body.liquorProducts;
      for (const product of catalog) {
        products.set(product.id, productInfoFrom(product));
      }
    } else {
      catalogFailed = true;
    }
  }

  function targetFor(product: { id: string; name: string }): ReceiveScanTargetDto {
    if (origin.kind === "order") {
      const onOrder = origin.lines.find((line) => line.liquorProductId === product.id);
      if (onOrder !== undefined) {
        const target: ReceiveScanOrderLineTargetDto = {
          type: "order_line",
          orderLineId: onOrder.purchaseOrderLineId,
          liquorProductId: onOrder.liquorProductId,
          productName: onOrder.productName,
          orderedQuantity: onOrder.orderedQuantity,
          receivedQuantity: onOrder.receivedQuantity,
          outstandingQuantity: onOrder.outstandingQuantity,
          orderUnit: onOrder.orderUnit ?? null,
          bottlesPerOrderUnit: onOrder.bottlesPerOrderUnit ?? null,
        };
        return target;
      }
    }
    return {
      type: "item",
      liquorProductId: product.id,
      productName: product.name,
      orderUnit: null,
      bottlesPerOrderUnit: null,
    };
  }

  let linkChip = "";
  let linkLetter = "";
  let linkSelected: string | undefined;

  function openLink(lineNumber: number): void {
    const line = lines.find((candidate) => candidate.lineNumber === lineNumber);
    if (line === undefined || line.posted) {
      return;
    }
    sheet = { kind: "link", lineNumber };
    linkChip = origin.kind === "order" && origin.lines.length > 0 ? "on_order" : "all";
    linkLetter = "";
    linkSelected = line.candidate?.liquorProductId ?? line.target?.liquorProductId;
    renderSheet();
    void ensureCatalog().then(() => renderSheet());
  }

  function closeSheet(): void {
    sheet = undefined;
    renderSheet();
  }

  function linkRows(): Array<{ id: string; name: string; note: string; category: string }> {
    const onOrderIds = new Set(
      origin.kind === "order" ? origin.lines.map((l) => l.liquorProductId) : [],
    );
    const rows: Array<{ id: string; name: string; note: string; category: string }> = [];
    if (origin.kind === "order") {
      for (const line of origin.lines) {
        rows.push({
          id: line.liquorProductId,
          name: line.productName,
          note: `Ordered ${formatReceivingQuantity(line.orderedQuantity)}`,
          category: "",
        });
      }
    }
    if (linkChip !== "on_order") {
      for (const product of catalog ?? []) {
        if (onOrderIds.has(product.id)) {
          continue;
        }
        rows.push({
          id: product.id,
          name: product.name,
          note: hasOrder() ? "Not on this order" : "",
          category: product.category,
        });
      }
    }
    const categoryOf = new Map((catalog ?? []).map((p) => [p.id, p.category]));
    return rows
      .map((row) => ({ ...row, category: row.category || (categoryOf.get(row.id) ?? "") }))
      .filter((row) => linkChip === "all" || linkChip === "on_order" || row.category === linkChip)
      .filter((row) => linkLetter === "" || row.name.trim().toUpperCase().startsWith(linkLetter))
      .sort((a, b) => a.name.localeCompare(b.name));
  }

  function renderLinkSheet(lineNumber: number): HTMLElement {
    const line = lines.find((candidate) => candidate.lineNumber === lineNumber);
    const sheetEl = el("div", "sheet receive-scan-sheet", "receive-scan-link-sheet");
    sheetEl.setAttribute("role", "dialog");
    sheetEl.setAttribute("aria-modal", "true");
    const head = el("div", "receive-scan-sheet-head");
    head.append(
      el("h2", "receive-scan-sheet-title", undefined, "Link to an item"),
      button("btn-secondary", "receive-scan-link-cancel", "Cancel", closeSheet),
    );
    sheetEl.append(head);
    if (line !== undefined) {
      sheetEl.append(
        el(
          "div",
          "receive-scan-source",
          "receive-scan-link-source",
          `${line.description}   qty ${line.invoiceQuantity ?? NO_VALUE}`,
        ),
      );
    }
    const chips = el("div", "receive-scan-chips");
    const chip = (key: string, label: string): void => {
      const node = button(
        `receive-scan-chip${linkChip === key ? " is-on" : ""}`,
        "receive-scan-link-chip",
        label,
        () => {
          linkChip = key;
          linkLetter = "";
          renderSheet();
        },
      );
      node.dataset["chip"] = key;
      chips.append(node);
    };
    if (origin.kind === "order" && origin.lines.length > 0) {
      chip("on_order", "On this order");
    }
    chip("all", "All");
    const categories = [...new Set((catalog ?? []).map((p) => p.category))].sort();
    for (const category of categories) {
      chip(category, category.charAt(0).toUpperCase() + category.slice(1));
    }
    sheetEl.append(chips);
    const allRows = (() => {
      const keep = linkLetter;
      linkLetter = "";
      const rows = linkRows();
      linkLetter = keep;
      return rows;
    })();
    const letters = [
      ...new Set(allRows.map((row) => row.name.trim().charAt(0).toUpperCase())),
    ].sort();
    const strip = el("div", "receive-scan-letters");
    for (const letter of letters) {
      const node = button(
        `receive-scan-letter${linkLetter === letter ? " is-on" : ""}`,
        "receive-scan-link-letter",
        letter,
        () => {
          linkLetter = linkLetter === letter ? "" : letter;
          renderSheet();
        },
      );
      strip.append(node);
    }
    sheetEl.append(strip);
    const list = el("div", "receive-scan-plist", "receive-scan-link-list");
    const rows = linkRows();
    if (rows.length === 0) {
      list.append(
        el("div", "receive-scan-na", "receive-scan-link-empty", catalogFailed ? "The product list could not be loaded. Try again." : "No products to link yet."),
      );
    }
    for (const row of rows) {
      const node = button(
        `receive-scan-row${linkSelected === row.id ? " is-selected" : ""}`,
        "receive-scan-link-row",
        "",
        () => {
          linkSelected = row.id;
          renderSheet();
        },
      );
      node.dataset["productId"] = row.id;
      node.append(el("b", "", undefined, row.name), el("small", "", undefined, row.note));
      list.append(node);
    }
    sheetEl.append(list);
    const foot = el("div", "receive-scan-sheet-foot");
    foot.append(
      button("btn-secondary", "receive-scan-link-notreceived", "Mark not received", () => {
        replaceLine(lineNumber, markNotReceived);
        closeSheet();
      }),
    );
    const quantity = line?.quantity ?? "0";
    const confirm = button(
      "btn-primary",
      "receive-scan-link-confirm",
      `Link and add ${formatReceivingQuantity(quantity)}`,
      () => {
        const picked =
          rows.find((row) => row.id === linkSelected) ??
          (catalog ?? [])
            .map((p) => ({ id: p.id, name: p.name }))
            .find((p) => p.id === linkSelected);
        if (picked === undefined) {
          return;
        }
        replaceLine(lineNumber, (current) => linkLine(current, targetFor(picked)));
        closeSheet();
      },
    );
    confirm.disabled = linkSelected === undefined || !rows.some((row) => row.id === linkSelected);
    foot.append(confirm);
    sheetEl.append(foot);
    return sheetEl;
  }

  function renderVendorSheet(): HTMLElement {
    const sheetEl = el("div", "sheet receive-scan-sheet", "receive-scan-vendor-sheet");
    sheetEl.setAttribute("role", "dialog");
    sheetEl.setAttribute("aria-modal", "true");
    const head = el("div", "receive-scan-sheet-head");
    head.append(
      el("h2", "receive-scan-sheet-title", undefined, "Pick a vendor"),
      button("btn-secondary", "receive-scan-vendor-cancel", "Cancel", closeSheet),
    );
    sheetEl.append(head);
    const list = el("div", "receive-scan-plist", "receive-scan-vendor-list");
    const vendors = draft?.vendors ?? [];
    if (vendors.length === 0) {
      list.append(el("div", "receive-scan-na", undefined, "No vendors yet."));
    }
    for (const vendor of vendors) {
      const node = button("receive-scan-row", "receive-scan-vendor-row", "", () => {
        void confirmVendor(vendor.id);
      });
      node.dataset["vendorId"] = vendor.id;
      node.append(el("b", "", undefined, vendor.name));
      list.append(node);
    }
    sheetEl.append(list);
    return sheetEl;
  }

  function renderDiscardSheet(): HTMLElement {
    const sheetEl = el("div", "sheet receive-scan-sheet is-center", "receive-scan-discard-sheet");
    sheetEl.setAttribute("role", "dialog");
    sheetEl.setAttribute("aria-modal", "true");
    sheetEl.append(el("h2", "receive-scan-sheet-title", undefined, "Discard this receive?"));
    const actions = el("div", "receive-scan-sheet-foot");
    actions.append(
      button("btn-secondary", "receive-scan-discard-keep", "Keep working", closeSheet),
      button("btn-primary", "receive-scan-discard-confirm", "Discard", () => {
        closeSheet();
        leave();
      }),
    );
    sheetEl.append(actions);
    return sheetEl;
  }

  function renderSheet(): void {
    backdrop.replaceChildren();
    if (sheet === undefined) {
      backdrop.hidden = true;
      return;
    }
    backdrop.hidden = false;
    if (sheet.kind === "link") {
      backdrop.append(renderLinkSheet(sheet.lineNumber));
    } else if (sheet.kind === "vendor") {
      backdrop.append(renderVendorSheet());
    } else {
      backdrop.append(renderDiscardSheet());
    }
  }

  // ---- header and leaving -------------------------------------------------------------------------------------

  function hasWork(): boolean {
    return view !== "done" && (pages.length > 0 || lines.some((line) => line.status !== "pending"));
  }

  function leave(): void {
    const where = origin;
    stop();
    deps.onLeave(where);
  }

  function onBack(): void {
    if (finishing) {
      return;
    }
    if (hasWork()) {
      sheet = { kind: "discard" };
      renderSheet();
      return;
    }
    leave();
  }

  function renderHeader(): void {
    header.replaceChildren();
    const back = button("receive-scan-back", "receive-scan-back", "", onBack);
    back.append(
      el("span", "receive-scan-back-chevron", undefined, "‹"),
      document.createTextNode(" Back"),
    );
    header.append(back, el("h1", "receive-scan-title", "receive-scan-title", "Receive"));
    header.append(
      tag(
        origin.kind === "order" ? origin.vendorName : "No purchase order",
        "",
        "receive-scan-origin-tag",
      ),
    );
    if (view === "review" && barcode === "off" && !finishing) {
      header.append(el("span", "receive-scan-spacer"));
      const scan = button("btn-secondary", "receive-scan-barcode", "", () => {
        void startBarcode();
      });
      scan.append(icon(ICON_BARCODE), document.createTextNode("Scan barcode"));
      header.append(scan);
    }
  }

  // ---- left card ----------------------------------------------------------------------------------------------

  function renderLeft(): void {
    left.replaceChildren();
    if (view === "start") {
      const c = card("Ordered", [
        tag(origin.kind === "order" ? `${origin.lines.length} lines` : NO_VALUE, ""),
      ]);
      const rows = el("div", "receive-scan-rows");
      if (origin.kind === "order" && origin.lines.length > 0) {
        for (const line of origin.lines) {
          const row = el("div", "receive-scan-simple-row");
          row.append(
            el("div", "receive-scan-line-name", undefined, line.productName),
            el(
              "div",
              "receive-scan-ordered",
              undefined,
              formatReceivingQuantity(line.orderedQuantity),
            ),
          );
          rows.append(row);
        }
      } else {
        rows.append(el("div", "receive-scan-na", undefined, "No order lines to show."));
      }
      c.append(rows);
      left.append(c);
      return;
    }
    if (view === "capture") {
      const c = card(
        `Page ${replacing !== undefined ? replacing + 1 : pages.length + 1}`,
        [
          tag(
            camera === "on" ? "Camera on" : camera === "blocked" ? "Camera off" : "Starting",
            camera === "on" ? "ok" : "warn",
            "receive-scan-camera-status",
          ),
        ],
        [
          button("btn-secondary", "receive-scan-camera-close", "Close camera", () => {
            stopCamera();
            view = pages.length > 0 ? "pages" : "start";
            replacing = undefined;
            render();
          }),
        ],
      );
      const stage = el("div", `receive-scan-cam${camera === "on" ? " is-on" : ""}`);
      if (camera === "blocked") {
        const blocked = el("div", "receive-scan-blocked", "receive-scan-camera-blocked");
        blocked.append(
          el("h3", "", undefined, "Camera is blocked"),
          button("btn-primary", "receive-scan-camera-retry", "Try again", () => {
            void startCamera();
          }),
          button("btn-secondary", "receive-scan-camera-file", "Choose file", chooseFile),
        );
        stage.append(blocked);
      } else {
        stage.append(video);
        const corners = el("div", "receive-scan-corners");
        corners.append(el("i", ""), el("i", ""), el("i", ""), el("i", ""));
        stage.append(corners);
        if (canFlip) {
          stage.append(
            button("receive-scan-flip", "receive-scan-flip", "Flip", () => {
              facing = facing === "environment" ? "user" : "environment";
              void startCamera();
            }),
          );
        }
        const shutter = button("receive-scan-shutter", "receive-scan-shutter", "", () => {
          void snap();
        });
        shutter.setAttribute("aria-label", "Take photo");
        shutter.disabled = camera !== "on";
        shutter.append(el("i", ""));
        stage.append(shutter);
      }
      c.append(stage);
      left.append(c);
      return;
    }
    if (view === "pages" || view === "failed") {
      const page = pages[selectedPage];
      const c = card(
        page === undefined ? "Pages" : `Page ${selectedPage + 1}`,
        page === undefined ? [] : [tag("Captured", "")],
      );
      const stage = el("div", "receive-scan-preview");
      if (page === undefined) {
        stage.append(el("div", "receive-scan-na", undefined, "No page captured yet. Photograph the invoice."));
      } else if (page.kind === "pdf") {
        stage.append(el("div", "receive-scan-na", undefined, page.file.name));
      } else {
        stage.append(cloneCanvas(page.preview, "receive-scan-preview-canvas"));
      }
      c.append(stage);
      const foot = el("div", "receive-scan-foot");
      foot.append(
        button(
          "btn-secondary receive-scan-wide",
          "receive-scan-retake",
          `Retake page ${selectedPage + 1}`,
          () => {
            if (page !== undefined && page.kind === "image") {
              openCamera(selectedPage);
            } else {
              chooseFile();
            }
          },
        ),
        button("btn-secondary receive-scan-wide", "receive-scan-add-page", "Add page", () =>
          openCamera(),
        ),
      );
      if (page === undefined) {
        foot.hidden = true;
      }
      c.append(foot);
      left.append(c);
      return;
    }
    if (view === "reading" || view === "vendor") {
      const c = card("Lines", [
        tag(view === "vendor" ? lineCountText(draft?.lines.length ?? 0) : NO_VALUE, ""),
      ]);
      if (view === "reading") {
        const skeleton = el("div", "receive-scan-skeletons");
        for (let i = 0; i < 6; i += 1) {
          skeleton.append(el("div", "receive-scan-skeleton"));
        }
        c.append(skeleton);
      } else {
        c.append(el("div", "receive-scan-na", undefined, "Lines appear here once the invoice is read."));
      }
      left.append(c);
      return;
    }
    if (view === "review") {
      renderReviewLeft();
      return;
    }
    if (view === "done") {
      const c = card("Receive finished", [tag("Saved", "ok")]);
      const done = el("div", "receive-scan-done", "receive-scan-done");
      const okc = el("div", "receive-scan-okc");
      okc.append(icon(ICON_CHECK));
      done.append(okc, el("h3", "", undefined, "Stock updated"));
      const stats = el("div", "receive-scan-stats");
      const figures: Array<[string, number, string]> = [
        ["Received in full", summary?.full ?? 0, "receive-scan-done-full"],
        ["Received short", summary?.short ?? 0, "receive-scan-done-short"],
        ["Not received", summary?.notReceived ?? 0, "receive-scan-done-not"],
      ];
      for (const [label, value, testId] of figures) {
        if (!hasOrder() && label === "Received short") {
          continue;
        }
        const box = el("div", "receive-scan-stat", testId);
        box.append(el("b", "", undefined, String(value)), el("span", "", undefined, label));
        stats.append(box);
      }
      done.append(stats);
      c.append(done);
      left.append(c);
    }
  }

  function cloneCanvas(source: HTMLCanvasElement, className: string): HTMLCanvasElement {
    const copy = document.createElement("canvas");
    copy.width = source.width;
    copy.height = source.height;
    copy.className = className;
    copy.getContext("2d")?.drawImage(source, 0, 0);
    return copy;
  }

  function renderReviewLeft(): void {
    const total = lines.length;
    const handled = handledCount(lines);
    const c = card("Lines", [
      tag(
        lineCountText(total),
        lines.some((l) => l.lowConfidence && l.status === "pending") ? "warn" : "",
      ),
    ]);
    const rows = el("div", "receive-scan-rows", "receive-scan-lines");
    const head = el("div", "receive-scan-row-grid is-head");
    head.append(
      el("span", "", undefined, "Item"),
      el("span", "is-center", undefined, "Ordered"),
      el("span", "is-center", undefined, "Received"),
      el("span", ""),
    );
    rows.append(head);
    if (total === 0) {
      rows.append(el("div", "receive-scan-na", "receive-scan-no-lines", "No lines were found on this invoice."));
    }
    for (const line of lines) {
      rows.append(renderLine(line));
    }
    c.append(rows);
    const foot = el("div", "receive-scan-foot");
    const sum = el("span", "receive-scan-sum", "receive-scan-handled");
    sum.append(
      el("b", "", undefined, String(handled)),
      document.createTextNode(` of ${total} handled`),
    );
    const blocker = finishBlocker(lines, finishContext());
    if (blocker !== null && blocker !== "Lines left" && blocker !== "No lines") {
      sum.append(el("span", "receive-scan-blocker", "receive-scan-blocker", blocker));
    }
    const finishButton = button(
      "btn-primary",
      "receive-scan-finish",
      finishing ? "Recording" : "Finish receive",
      () => {
        void finish();
      },
    );
    finishButton.disabled = blocker !== null || finishing;
    foot.append(sum, finishButton);
    c.append(foot);
    left.append(c);
  }

  function renderLine(line: ScanLine): HTMLElement {
    const marks = lineMarks(line, { hasOrder: hasOrder() });
    const unmatched = line.target === null;
    const row = el(
      "div",
      `receive-scan-row-grid${unmatched ? " is-unmatched" : ""}${marks.includes("check_quantity") ? " is-low" : ""}${line.status === "accepted" ? " is-done" : ""}${line.status === "not_received" ? " is-skipped" : ""}`,
      "receive-scan-line",
    );
    row.dataset["lineNumber"] = String(line.lineNumber);
    row.dataset["status"] = line.status;
    const name = el("div", "receive-scan-line-cell");
    name.append(
      el(
        "div",
        "receive-scan-line-name",
        "receive-scan-line-name",
        line.target === null ? line.description : line.target.productName,
      ),
      el(
        "small",
        "receive-scan-line-sub",
        "receive-scan-line-sub",
        line.target === null
          ? `Invoice line ${line.lineNumber}, qty ${line.invoiceQuantity ?? NO_VALUE}`
          : `Invoice: ${line.description}`,
      ),
    );
    const markText = [...(unmatched ? ["No match"] : []), ...marks.map((mark) => MARK_TEXT[mark])];
    for (const text of markText) {
      const mark = el("span", "receive-scan-mark", "receive-scan-mark");
      mark.append(icon(ICON_WARN), document.createTextNode(` ${text}`));
      name.append(mark);
    }
    if (line.error !== null) {
      name.append(el("div", "receive-scan-line-error", "receive-scan-line-error", line.error));
    }
    if (line.status === "not_received") {
      name.append(el("div", "receive-scan-skipped", "receive-scan-not-received", "Not received"));
    }
    if (unmatched) {
      row.append(name);
      const actions = el("div", "receive-scan-line-actions");
      if (line.status === "not_received") {
        actions.append(
          button("btn-secondary", "receive-scan-line-undo", "Undo", () =>
            replaceLine(line.lineNumber, undoLine),
          ),
        );
      } else {
        actions.append(
          button("btn-primary", "receive-scan-line-link", "Link to item", () =>
            openLink(line.lineNumber),
          ),
          button("btn-secondary", "receive-scan-line-not-received", "Not received", () =>
            replaceLine(line.lineNumber, markNotReceived),
          ),
        );
      }
      row.append(actions);
      return row;
    }
    row.append(name);
    const ordered = el(
      "div",
      "receive-scan-ordered",
      "receive-scan-line-ordered",
      line.target?.type === "order_line"
        ? formatReceivingQuantity(line.target.outstandingQuantity)
        : NO_VALUE,
    );
    row.append(ordered);
    const stepper = el("div", "receive-scan-stepper");
    const minus = button("receive-scan-step", "receive-scan-line-minus", "−", () =>
      replaceLine(line.lineNumber, (current) => stepLine(current, -1)),
    );
    minus.setAttribute("aria-label", "Fewer");
    const plus = button("receive-scan-step", "receive-scan-line-plus", "+", () =>
      replaceLine(line.lineNumber, (current) => stepLine(current, 1)),
    );
    plus.setAttribute("aria-label", "More");
    const qty = el(
      "div",
      "receive-scan-qty",
      "receive-scan-line-qty",
      formatReceivingQuantity(line.quantity),
    );
    minus.disabled = line.posted;
    plus.disabled = line.posted;
    const middle = el("div", "receive-scan-qty-wrap");
    middle.append(qty);
    const packed = packedReading(line);
    if (packed !== "") {
      middle.append(el("small", "receive-scan-packed", "receive-scan-line-packed", packed));
    }
    stepper.append(minus, middle, plus);
    row.append(stepper);
    const accepted = line.status === "accepted";
    // A quantity of zero means nothing arrived on this line: the tap that would be Accept is Not received.
    const nothingArrived = line.status === "pending" && isZeroQuantity(line);
    const accept = button("receive-scan-accept", "receive-scan-line-accept", "", () =>
      replaceLine(line.lineNumber, (current) => {
        if (current.status === "accepted") {
          return undoLine(current);
        }
        return isZeroQuantity(current) ? markNotReceived(current) : acceptLine(current);
      }),
    );
    if (accepted) {
      accept.append(icon(ICON_CHECK), document.createTextNode(" Accepted"));
    } else {
      accept.textContent = nothingArrived ? "Not received" : "Accept";
    }
    accept.disabled =
      line.posted ||
      line.status === "not_received" ||
      (!accepted && !nothingArrived && !canAccept(line));
    if (line.status === "not_received") {
      row.append(
        button("receive-scan-accept", "receive-scan-line-undo", "Undo", () =>
          replaceLine(line.lineNumber, undoLine),
        ),
      );
    } else {
      row.append(accept);
    }
    return row;
  }

  // ---- right card ---------------------------------------------------------------------------------------------

  function renderRight(): void {
    right.replaceChildren();
    if (view === "start") {
      const c = card("Receive");
      const big = el("div", "receive-scan-big");
      const scan = button("btn-primary", "receive-scan-start-scan", "", () => openCamera());
      scan.append(icon(ICON_CAMERA), document.createTextNode("Scan invoice"));
      const file = button("btn-secondary", "receive-scan-start-file", "", chooseFile);
      file.append(icon(ICON_FILE), document.createTextNode("Choose file"));
      const hand = button("btn-secondary", "receive-scan-start-hand", "Count by hand", leave);
      big.append(scan, file, hand);
      c.append(big);
      if (notice !== null) {
        c.append(el("p", "receive-scan-notice", "receive-scan-notice", notice));
      }
      right.append(c);
      return;
    }
    if (view === "capture" || view === "pages") {
      const c = card("Pages", [tag(String(pages.length), "")]);
      const grid = el("div", "receive-scan-pages", "receive-scan-pages");
      pages.forEach((page, index) => {
        const item = el(
          "div",
          `receive-scan-page${index === selectedPage && view === "pages" ? " is-selected" : ""}`,
          "receive-scan-page",
        );
        item.dataset["pageIndex"] = String(index);
        const thumb = button("receive-scan-thumb", "receive-scan-thumb", "", () => {
          selectedPage = index;
          if (view === "pages") {
            renderLeft();
            renderRight();
          }
        });
        thumb.append(
          page.kind === "image"
            ? cloneCanvas(page.preview, "receive-scan-thumb-canvas")
            : el("div", "receive-scan-na", undefined, "PDF"),
        );
        const cap = el("div", "receive-scan-page-cap");
        cap.append(
          el("span", "", undefined, `Page ${index + 1}`),
          button("btn-secondary", "receive-scan-page-retake", "Retake", () => {
            if (page.kind === "image") {
              openCamera(index);
            } else {
              chooseFile();
            }
          }),
        );
        item.append(thumb, cap);
        grid.append(item);
      });
      if (pages.length > 0 && pages[0]?.kind !== "pdf") {
        grid.append(
          button("receive-scan-add", "receive-scan-add", "+ Add page", () => openCamera()),
        );
      }
      c.append(grid);
      if (notice !== null) {
        c.append(el("p", "receive-scan-notice", "receive-scan-notice", notice));
      }
      const foot = el("div", "receive-scan-foot");
      const read = button(
        "btn-primary receive-scan-wide",
        "receive-scan-read",
        "Read invoice",
        () => {
          void readInvoice();
        },
      );
      read.disabled = pages.length === 0 || working;
      foot.append(read);
      c.append(foot);
      right.append(c);
      return;
    }
    if (view === "reading") {
      const c = card("Invoice", [tag(pageCountText(pages.length), "")]);
      const reading = el("div", "receive-scan-reading", "receive-scan-reading");
      reading.append(
        el("div", "receive-scan-spinner"),
        el("h3", "", undefined, "Reading invoice"),
        button(
          "btn-secondary receive-scan-cancel",
          "receive-scan-reading-cancel",
          "Cancel",
          cancelReading,
        ),
      );
      c.append(reading);
      right.append(c);
      return;
    }
    if (view === "failed") {
      const c = card("Invoice", [tag(pageCountText(pages.length), "warn")]);
      const failed = el("div", "receive-scan-reading", "receive-scan-failed");
      failed.append(
        el(
          "h3",
          "",
          "receive-scan-failed-message",
          readingMessage ?? "This invoice could not be read",
        ),
      );
      if (readingRetryable) {
        failed.append(
          button("btn-primary receive-scan-cancel", "receive-scan-retry", "Retry", () => {
            if (uploadedVersion !== pagesVersion || attachmentId === null) {
              void readInvoice();
            } else {
              void retryReading();
            }
          }),
        );
      }
      failed.append(
        button("btn-secondary receive-scan-cancel", "receive-scan-failed-retake", "Retake", () => {
          view = "pages";
          render();
        }),
        button(
          "btn-secondary receive-scan-cancel",
          "receive-scan-failed-hand",
          "Count by hand",
          leave,
        ),
      );
      c.append(failed);
      right.append(c);
      return;
    }
    if (view === "vendor") {
      const c = card("Vendor");
      const box = el("div", "receive-scan-big");
      const suggested = draft?.vendors.find((v) => v.id === draft?.suggestedVendorId);
      box.append(
        el(
          "div",
          "receive-scan-vendor-name",
          "receive-scan-vendor-name",
          suggested?.name ?? draft?.vendorReadName ?? NO_VALUE,
        ),
      );
      if (suggested !== undefined) {
        const confirm = button(
          "btn-primary",
          "receive-scan-vendor-confirm",
          "Confirm vendor",
          () => {
            void confirmVendor(suggested.id);
          },
        );
        confirm.disabled = working;
        box.append(confirm);
      }
      box.append(
        button("btn-secondary", "receive-scan-vendor-pick", "Pick another vendor", () => {
          sheet = { kind: "vendor" };
          renderSheet();
        }),
      );
      c.append(box);
      if (notice !== null) {
        c.append(el("p", "receive-scan-notice", "receive-scan-notice", notice));
      }
      right.append(c);
      return;
    }
    if (view === "review") {
      if (barcode !== "off") {
        renderBarcodeRight();
      } else {
        renderReviewRight();
      }
      return;
    }
    if (view === "done") {
      const c = card("Invoice", [
        tag(
          origin.kind === "order" ? "Not reviewed" : "Recorded",
          origin.kind === "order" ? "warn" : "ok",
        ),
      ]);
      c.append(
        field(
          "Invoice number",
          draft?.invoiceNumber ?? NO_VALUE,
          false,
          "receive-scan-done-number",
        ),
      );
      c.append(field("Pages", String(pages.length), false));
      c.append(field("Lines", String(lines.length), false));
      const big = el("div", "receive-scan-big");
      if (
        origin.kind === "order" &&
        deps.hasPermission(COST_PERMISSION) &&
        deps.hasPermission("inv.receipt.view")
      ) {
        const orderId = origin.orderId;
        big.append(
          button("btn-primary", "receive-scan-review-invoice", "Review invoice", () => {
            stop();
            deps.onReviewInvoice(orderId);
          }),
        );
      }
      big.append(button("btn-secondary", "receive-scan-done-close", "Done", leave));
      c.append(big);
      right.append(c);
    }
  }

  function field(label: string, value: string, low: boolean, testId?: string): HTMLElement {
    const row = el("div", `receive-scan-field${low ? " is-low" : ""}`);
    row.append(el("span", "", undefined, label), el("b", "", testId, low ? `${value} ?` : value));
    return row;
  }

  function renderBarcodeRight(): void {
    const c = card(
      "Scan",
      [
        tag(
          barcode === "on" ? "Camera on" : barcode === "blocked" ? "Camera off" : "Starting",
          barcode === "on" ? "ok" : "warn",
          "receive-scan-barcode-status",
        ),
      ],
      [
        button("btn-secondary", "receive-scan-barcode-close", "Close camera", () => {
          stopBarcode();
          renderHeader();
          renderRight();
        }),
      ],
    );
    if (barcode === "blocked") {
      const blocked = el("div", "receive-scan-blocked is-light", "receive-scan-barcode-blocked");
      blocked.append(
        el("h3", "", undefined, "Camera is blocked"),
        button("btn-primary", "receive-scan-barcode-retry", "Try again", () => {
          void startBarcode();
        }),
      );
      c.append(blocked);
    } else {
      const mini = el("div", "receive-scan-mini");
      mini.append(barcodeVideo);
      const frame = el("div", "receive-scan-mini-frame");
      frame.append(el("i", ""), el("i", ""), el("i", ""), el("i", ""));
      mini.append(frame);
      c.append(mini);
      if (barcodeHit !== null) {
        const hit = el("div", "receive-scan-hit", "receive-scan-barcode-hit");
        hit.append(
          el("b", "", "receive-scan-barcode-hit-name", barcodeHit.name),
          el(
            "span",
            "receive-scan-hit-n",
            "receive-scan-barcode-hit-progress",
            barcodeHit.progress,
          ),
        );
        c.append(hit);
      } else if (barcodeHint !== "") {
        c.append(el("p", "receive-scan-notice", "receive-scan-barcode-hint", barcodeHint));
      }
    }
    right.append(c);
    if (barcode === "on") {
      void barcodeVideo.play().catch(() => undefined);
    }
  }

  function renderReviewRight(): void {
    const c = card(
      "Invoice",
      [tag(pageCountText(pages.length), "")],
      [
        ...(pages.length > 1
          ? [
              button("btn-secondary", "receive-scan-next-page", "Next page", () => {
                previewIndex = (previewIndex + 1) % pages.length;
                renderRight();
              }),
            ]
          : []),
      ],
    );
    const vendorText =
      origin.kind === "order"
        ? (draft?.orderVendorName ?? NO_VALUE)
        : (draft?.vendors.find((v) => v.id === vendorId)?.name ?? NO_VALUE);
    c.append(field("Vendor", vendorText, false, "receive-scan-vendor"));
    c.append(
      field(
        "Invoice number",
        draft?.invoiceNumber ?? NO_VALUE,
        draft?.invoiceNumberLowConfidence === true,
        "receive-scan-invoice-number",
      ),
    );
    c.append(field("Date", draft?.invoiceDate ?? NO_VALUE, false, "receive-scan-invoice-date"));
    if (draft?.duplicateRecorded === true) {
      const banner = el("div", "receive-scan-duplicate", "receive-scan-duplicate");
      banner.append(icon(ICON_WARN), document.createTextNode(" Already recorded"));
      c.append(banner);
    } else if (draft?.duplicateOnOrder === true) {
      const banner = el("div", "receive-scan-duplicate is-quiet", "receive-scan-duplicate-number");
      banner.append(icon(ICON_WARN), document.createTextNode(" Same number as an earlier invoice"));
      c.append(banner);
    }
    const stage = el("div", "receive-scan-paper");
    const page = pages[previewIndex] ?? pages[0];
    if (page !== undefined && page.kind === "image") {
      stage.append(cloneCanvas(page.preview, "receive-scan-preview-canvas"));
    } else {
      stage.append(el("div", "receive-scan-na", undefined, NO_VALUE));
    }
    c.append(stage);
    right.append(c);
  }

  // ---- whole screen -------------------------------------------------------------------------------------------

  function render(): void {
    renderHeader();
    renderLeft();
    renderRight();
    renderSheet();
  }

  function stop(): void {
    pollToken += 1;
    stopCamera();
    stopBarcode();
    isOpen = false;
    pages = [];
    selectedPage = 0;
    pagesVersion = 0;
    uploadedVersion = -1;
    uploadKey = null;
    attachmentId = null;
    working = false;
    notice = null;
    draft = null;
    vendorId = null;
    lines = [];
    summary = null;
    sheet = undefined;
    finishing = false;
    replacing = undefined;
    previewIndex = 0;
    view = "start";
    backdrop.hidden = true;
    backdrop.replaceChildren();
  }

  return {
    open(next) {
      stop();
      origin = next;
      isOpen = true;
      view = "start";
      catalog = undefined;
      catalogFailed = false;
      products.clear();
      if (!deps.hasPermission(RECEIPT_PERMISSION)) {
        isOpen = false;
        return;
      }
      render();
    },
    stop,
    countProduct(liquorProductId, delta) {
      if (!isOpen || view !== "review") {
        return;
      }
      lines = countScannedUnit(lines, liquorProductId, delta);
      renderLeft();
    },
  };
}
